import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AppConfigState } from '@nibtravel/shared/util/app-config';
import {
  EcommerceProduct,
  EcommerceService,
  ProductCategory,
} from '@nibtravel/shared/util/gtm';
import { ISpecifiedItemInput } from '@nibtravel/shared/util/models';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { PurchasePathDimensions } from '../custom-dimension/purchase-path-dimension.service';
import { PurchasePath } from './purchase-path.selectors';

@Injectable()
export class ItemAnalyticsService {
  readonly specifiedItemName = 'Specified items';
  readonly previousItemProduct: Record<string, EcommerceProduct | undefined> =
    {};

  constructor(
    private store: Store,
    private dimensionService: PurchasePathDimensions,
    private ecommerceService: EcommerceService
  ) {}

  addToCart(item: ISpecifiedItemInput): void {
    const itemProduct = this.getItemProduct(item);
    if (!itemProduct) {
      return;
    }
    const previousInput = this.previousItemProduct[item.guid];
    if (previousInput) {
      this.ecommerceService.removeFromCart([previousInput]);
    }
    this.dimensionService
      .getDimension()
      .pipe(take(1))
      .subscribe((dimension) => {
        this.previousItemProduct[item.guid] = {
          ...itemProduct,
          ...dimension,
        };
        this.ecommerceService.addToCart([{ ...itemProduct, ...dimension }]);
      });
  }

  removeFromCart(item: ISpecifiedItemInput): void {
    const itemProduct = this.getItemProduct(item);
    if (!itemProduct) {
      return;
    }

    this.dimensionService
      .getDimension()
      .pipe(take(1))
      .subscribe((dimension) => {
        const previousInput = this.previousItemProduct[item.guid];
        if (previousInput) {
          this.ecommerceService.removeFromCart([previousInput]);
          delete this.previousItemProduct[item.guid];
        } else {
          this.ecommerceService.removeFromCart([
            { ...itemProduct, ...dimension },
          ]);
        }
      });
  }

  getProducts(): Observable<EcommerceProduct[]> {
    return combineLatest([
      this.getSpecifiedItemProducts(),
      this.dimensionService.getDimension(),
    ]).pipe(
      map(([itemProducts, dimensions]) =>
        (itemProducts.filter((p) => !!p) as EcommerceProduct[]).map((ip) => {
          return { ...ip, ...dimensions };
        })
      )
    );
  }
  private getSpecifiedItemProducts(): Observable<EcommerceProduct[]> {
    return this.store.select(PurchasePath.specifiedItemOptions).pipe(
      map((specifiedItems: ISpecifiedItemInput[] | undefined) => {
        if (!specifiedItems) {
          return [];
        }
        return specifiedItems
          .filter((si) => !!si)
          .map((si) => this.getItemProduct(si)) as EcommerceProduct[];
      })
    );
  }

  private getItemProduct(
    item: ISpecifiedItemInput
  ): EcommerceProduct | undefined {
    const specifiedItemTypes = this.store.selectSnapshot(
      PurchasePath.specifiedItemTypes
    );
    if (!specifiedItemTypes) {
      return undefined;
    }

    const itemPrices = this.store.selectSnapshot(
      PurchasePath.specifiedItemPrices
    );
    const price = itemPrices
      ? itemPrices.find((siop) => siop.guid === item.guid)?.price
      : undefined;

    if (!price) {
      return undefined;
    }

    const itemType = specifiedItemTypes.find(
      (it) => it.specifiedItemTypeId === item.itemTypeId
    );
    const brand = this.store.selectSnapshot(AppConfigState.getBrand);

    return {
      id: itemType ? itemType.name : undefined,
      name: this.specifiedItemName,
      brand: brand,
      category: ProductCategory.OptionsSpecifiedItems,
      variant: item.name,
      price: price,
      quantity: 1,
    };
  }
}
